import { useCallback, useMemo, useState, useEffect, useRef } from "react";
import { useSelector } from "react-redux";
import {
  useDeleteOmrUploadMutation,
  useListOmrUploadsQuery,
  useUploadOmrPdfMutation,
} from "./omrUploadApi";
import { formatDateToISTFull } from "../../shared/utils/formatDate";

const UPLOAD_SPEED_BYTES_PER_SEC = 1 * 1024 * 1024;

const normalizeGradeValue = (grade) => {
  const parsed = Number(grade);
  if (Number.isNaN(parsed)) {
    return grade;
  }
  return parsed;
};

const normalizeUploads = (uploads) => {
  if (!Array.isArray(uploads)) {
    return [];
  }

  return uploads.map((upload) => {
    const gradeValue = normalizeGradeValue(upload?.grade);
    const uploadedAtRaw = upload?.upload_time;
    const uploadedAtFormatted = formatDateToISTFull(uploadedAtRaw);

    return {
      id:
        upload?.job_id ||
        upload?.id ||
        `${upload?.file_name}-${upload?.upload_time}`,
      jobId: upload?.job_id,
      fileName: upload?.file_name,
      link: upload?.link,
      grade: gradeValue,
      uploadedAt: uploadedAtRaw,
      uploadedAtFormatted,
      uploadedBy: upload?.name,
      uploadStatus: upload?.upload_status,
      message: upload?.message,
      raw: upload,
    };
  });
};

const getEventId = (event) => {
  if (!event) return null;
  return event?.scheduled_events?.[0];
};

const getSchoolId = (school) => {
  if (!school) return null;
  return (
    school.school_id ||
    school.id ||
    school?.schoolId ||
    school?.school_code ||
    null
  );
};

const useOmrUploadData = ({
  fetchUploads = true,
  gradeId,
  page = 1,
  limit = 10,
} = {}) => {
  const { selectedSchool, selectedEvent } = useSelector(
    (state) => state.omrUpload
  );

  const schoolId = getSchoolId(selectedSchool);
  const eventId = getEventId(selectedEvent);
  const gradeProvided = gradeId !== undefined;
  const resolvedGradeId = gradeProvided ? gradeId : null;
  const hasGradeValue =
    gradeProvided &&
    resolvedGradeId !== null &&
    resolvedGradeId !== undefined &&
    resolvedGradeId !== "";

  const grades = useMemo(() => {
    const scheduledGrades = selectedEvent?.scheduled_grades || [];
    return scheduledGrades
      .map((grade) => normalizeGradeValue(grade))
      .filter((grade) => !Number.isNaN(Number(grade)))
      .sort((a, b) => Number(a) - Number(b));
  }, [selectedEvent]);

  const shouldFetchUploads =
    fetchUploads &&
    Boolean(schoolId && eventId) &&
    (!gradeProvided || hasGradeValue);

  const listUploadsArgs = useMemo(
    () => ({
      schoolId,
      eventId,
      gradeId: hasGradeValue ? resolvedGradeId : undefined,
      page,
      limit,
    }),
    [schoolId, eventId, hasGradeValue, resolvedGradeId, page, limit]
  );

  const {
    data: uploadsResponse,
    isLoading: isUploadsLoading,
    isFetching: isUploadsFetching,
    isError: isUploadsError,
    error: uploadsError,
    refetch: refetchUploads,
  } = useListOmrUploadsQuery(listUploadsArgs, {
    skip: !shouldFetchUploads,
  });

  const uploads = useMemo(
    () => normalizeUploads(uploadsResponse?.uploads),
    [uploadsResponse]
  );

  const [uploadOmrPdfMutation, uploadOmrPdfState] = useUploadOmrPdfMutation();
  const [deleteOmrUploadMutation, deleteOmrUploadState] =
    useDeleteOmrUploadMutation();

  // Progress simulation state
  const [uploadFileSize, setUploadFileSize] = useState(0);
  const [simulatedProgress, setSimulatedProgress] = useState(0);
  const uploadStartTimeRef = useRef(null);

  const uploadOmrPdf = useCallback(
    async ({ gradeId, file }) => {
      if (!gradeId || !file) {
        throw new Error("Missing grade or file for upload");
      }
      if (!schoolId || !eventId) {
        throw new Error("Missing school or event context");
      }

      setUploadFileSize(file.size);
      setSimulatedProgress(0);
      uploadStartTimeRef.current = Date.now();

      const formData = new FormData();
      formData.append("grade", gradeId);
      formData.append("fileobject", file);
      formData.append("school_id", schoolId);
      formData.append("event_id", eventId);

      return uploadOmrPdfMutation({ formData }).unwrap();
    },
    [eventId, schoolId, uploadOmrPdfMutation]
  );

  const deleteOmrUpload = useCallback(
    async (jobId) => {
      if (!jobId) {
        throw new Error("Missing job id");
      }
      return deleteOmrUploadMutation({ jobId }).unwrap();
    },
    [deleteOmrUploadMutation]
  );

  // Simulate upload progress based on file size and elapsed time
  useEffect(() => {
    if (!uploadOmrPdfState.isLoading || uploadFileSize === 0) {
      return;
    }

    const expectedDurationMs =
      (uploadFileSize / UPLOAD_SPEED_BYTES_PER_SEC) * 1000;

    const intervalId = setInterval(() => {
      const elapsed = Date.now() - uploadStartTimeRef.current;
      const progress = Math.min((elapsed / expectedDurationMs) * 100, 95);
      setSimulatedProgress(Math.round(progress));
    }, 100);

    return () => clearInterval(intervalId);
  }, [uploadOmrPdfState.isLoading, uploadFileSize]);

  useEffect(() => {
    if (!uploadOmrPdfState.isLoading && !uploadOmrPdfState.isSuccess) {
      setSimulatedProgress(0);
      setUploadFileSize(0);
      uploadStartTimeRef.current = null;
    }
  }, [uploadOmrPdfState.isLoading, uploadOmrPdfState.isSuccess]);

  const uploadProgress = useMemo(() => {
    if (uploadOmrPdfState.isSuccess) return 100;
    if (uploadOmrPdfState.isLoading) return simulatedProgress;
    return 0;
  }, [
    uploadOmrPdfState.isLoading,
    uploadOmrPdfState.isSuccess,
    simulatedProgress,
  ]);

  return {
    selectedSchool,
    selectedEvent,
    schoolId,
    eventId,
    grades,
    uploads,
    totalCount: uploadsResponse?.totalCount || 0,
    hasContext: Boolean(schoolId && eventId),
    isUploadsLoading: isUploadsLoading || isUploadsFetching,
    isUploadsError,
    uploadsError,
    refreshUploads: refetchUploads,
    uploadOmrPdf,
    uploadState: {
      ...uploadOmrPdfState,
      progress: uploadProgress,
    },
    deleteOmrUpload,
    deleteState: deleteOmrUploadState,
  };
};

export default useOmrUploadData;
